import React, { useEffect, useState } from 'react'
import { Box, Typography, Alert, CircularProgress } from '@mui/material'
import Parse from 'parse'
import { PasswordInput } from '../Inputs'
import { StyledButton } from '../CompanyManagement/styles/StyledComponents'
import authService from '../../services/authService'
import SecurityService, { type SecuritySettings } from '../../services/securityService'
import type { SignupConsent } from '../../consent/types'

export interface AccountPasswordPanelProps {
  googleLinked: boolean
  /**
   * Whether the account already has a password of its own, which decides between
   * "Change password" (asks for the current one) and "Set a password".
   *
   * Leave undefined to have the panel confirm it against the server. Callers reading
   * this from the cached `Parse.User.current()` can be stale — a password set earlier in
   * the session whose refetch failed leaves the cache saying false, which showed
   * "Set a password" to somebody who has one. Pass an explicit value only when you
   * already know the answer and want to skip the round trip, as the invitation flow
   * does. [SC-313]
   */
  hasManualPassword?: boolean
  /**
   * Called after the password is saved and the current user has been refetched.
   * Used by the invitation flow to advance past its mandatory password step; the
   * in-profile usage leaves it unset and just shows the success message.
   *
   * Not called when the password saved but no live session could be established —
   * advancing then would drop the user into an app where every request fails.
   */
  onSaved?: () => void
  /** Overrides the panel heading (the invitation flow frames this as onboarding). */
  title?: string
  /** Overrides the helper text under the heading. */
  description?: string
  /**
   * Rendered between the fields and the submit button. The invitation flow puts its
   * marketing checkbox and Terms notice here; Profile leaves it unset. [SC-360]
   */
  beforeSubmit?: React.ReactNode
  /**
   * Sent with the save so cloud records Terms and the marketing choice alongside the
   * password. Only the invitation flow sets it; without it the call is unchanged. [SC-360]
   */
  consent?: SignupConsent
}

/**
 * How we know whether the account already has a password.
 *
 * `given` — the caller told us. `loading` — asking the server. `resolved` — the server
 * answered. `failed` — we could not find out, so the form must say so rather than
 * silently rendering one of the two shapes and hoping.
 */
type PasswordProbe = 'given' | 'loading' | 'resolved' | 'failed'

/**
 * Put the tab back on a live, persisted session after a password save.
 *
 * Saving a password revokes every session the account had, including the one that made
 * the call. The cloud function mints a replacement and returns it, but that is not
 * guaranteed — an older cloud deploy has no such field, and the server-side re-login can
 * fail — so fall back to signing in here with the credentials we just saved. Returns
 * false only if both routes fail, in which case the caller must not pretend the user is
 * still signed in. [SC-313]
 *
 * Exported for tests: this is the branch that decides whether onboarding finishes on a
 * working session, and the jsdom-rendered tests cannot reach the no-token path.
 */
export async function adoptLiveSession(
  sessionToken: string | null | undefined,
  newPassword: string,
): Promise<boolean> {
  if (sessionToken) {
    try {
      await authService.adoptSessionToken(sessionToken)
      return true
    } catch (e) {
      console.warn('[AccountPasswordPanel] Replacement session token was rejected:', e)
    }
  }

  // Read the username off the cached user: the session is dead but the object is still
  // in memory, and guessing the username from the email would be wrong for any account
  // where the two differ.
  const username = Parse.User.current()?.getUsername()
  if (!username) return false
  try {
    const user = await Parse.User.logIn(username, newPassword)
    const minted = user.getSessionToken()
    if (!minted) return false
    await authService.adoptSessionToken(minted)
    return true
  } catch (e) {
    console.warn('[AccountPasswordPanel] Could not re-establish a session after the save:', e)
    return false
  }
}

const AccountPasswordPanel: React.FC<AccountPasswordPanelProps> = ({
  googleLinked,
  hasManualPassword,
  onSaved,
  title,
  description,
  beforeSubmit,
  consent,
}) => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [passwordPolicy, setPasswordPolicy] = useState<SecuritySettings['password'] | null>(null)

  const [resolvedHasManualPassword, setResolvedHasManualPassword] = useState(hasManualPassword)
  const [probe, setProbe] = useState<PasswordProbe>(
    hasManualPassword === undefined ? 'loading' : 'given',
  )

  // When the probe fails, guess from the account type rather than defaulting to
  // "Set a password": all but Google-only accounts reach this panel with a password
  // already, so that default told the majority the opposite of the truth. The server
  // is authoritative either way, so a wrong guess costs one rejected submit.
  const needsCurrentPassword =
    probe === 'failed' ? !googleLinked : resolvedHasManualPassword === true

  // Only when the caller did not state it: confirm against the server so the form cannot
  // be labelled from a stale cache. The server enforces the requirement regardless, so a
  // failure here degrades to a stated warning rather than a silent mislabel.
  useEffect(() => {
    if (hasManualPassword !== undefined) {
      setResolvedHasManualPassword(hasManualPassword)
      setProbe('given')
      return
    }
    let cancelled = false
    const current = Parse.User.current()
    if (!current) {
      setProbe('failed')
      return
    }
    setProbe('loading')
    current
      .fetch()
      .then((fresh) => {
        if (cancelled) return
        setResolvedHasManualPassword(fresh.get('hasManualPassword') === true)
        setProbe('resolved')
      })
      .catch(() => {
        if (!cancelled) setProbe('failed')
      })
    return () => {
      cancelled = true
    }
  }, [hasManualPassword])

  // Fetch the workspace policy so the user sees the real rules instead of
  // discovering them one submission at a time. The server re-validates regardless;
  // this is feedback only, so a fetch failure must not block the form.
  useEffect(() => {
    let cancelled = false
    SecurityService.getInstance()
      .getPasswordPolicy()
      .then((policy) => {
        if (!cancelled) setPasswordPolicy(policy)
      })
      .catch(() => {
        if (!cancelled) setPasswordPolicy(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (passwordPolicy) {
      const validation = SecurityService.getInstance().validatePasswordLocally(
        newPassword,
        { password: passwordPolicy } as SecuritySettings,
      )
      if (!validation.isValid) {
        setError(validation.errors.join(' '))
        return
      }
    } else if (newPassword.length < 8) {
      // Policy unavailable — fall back to the length floor rather than no check.
      setError('Password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (needsCurrentPassword && !currentPassword) {
      setError('Enter your current password.')
      return
    }

    setBusy(true)
    try {
      // authService leaves `consent` out of the request when it is undefined, so the Profile
      // save sends exactly what it did before SC-360.
      const result = await authService.setAccountPassword(
        newPassword,
        needsCurrentPassword ? currentPassword : undefined,
        consent,
      )

      // The save revoked the session that made the call, so get onto a live one before
      // anything else touches Parse. Skipping this produced an "Invalid session token"
      // error alongside the success message; persisting it is what stops the next
      // reload from restoring the revoked token and signing the user out. [SC-313]
      const sessionAlive = await adoptLiveSession(result?.sessionToken, newPassword)

      if (sessionAlive) {
        try {
          const cu = Parse.User.current()
          if (cu) {
            // Refetch so hasManualPassword is current before onSaved runs — callers
            // gate on that flag to decide whether the account still owes a password.
            await cu.fetch()
          }
        } catch {
          // Non-fatal: the password is saved and the session is live. Worst case the
          // cached copy of the flag lags until the next load.
        }
      }

      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      // The account definitely has its own password now, whatever the probe said.
      setResolvedHasManualPassword(true)
      setProbe('resolved')

      if (!sessionAlive) {
        // Saved, but we could not put the tab back on a working session. Say so instead
        // of advancing — the invitation flow would otherwise hand the user a dashboard
        // where every request fails. Signing in works: setAccountPassword succeeded, so the
        // password is set and the invitation token has already been cleared server-side.
        setSuccess('Password saved. Please sign in again with your new password.')
        return
      }

      // Set last, so a later failure can never render success and error together.
      setSuccess('Password saved. You can sign in with email and password anytime.')
      onSaved?.()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not update password'
      setSuccess(null)
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  // Nothing to label the form with yet. Rendering the fields now would flash the wrong
  // heading — and, before the probe reported failure separately, could leave it there.
  if (probe === 'loading') {
    return (
      <Box sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Checking your account…
        </Typography>
      </Box>
    )
  }

  return (
    <Box component="form" onSubmit={(e) => void handleSubmit(e)} sx={{ py: 1, maxWidth: 420 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {title ?? (needsCurrentPassword ? 'Change password' : 'Set a password')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description ??
          (googleLinked && !needsCurrentPassword
            ? 'Your account uses Google Sign-In. Add a password here if you also want to sign in with email and password.'
            : 'Use a strong password with at least 8 characters.')}
      </Typography>

      {probe === 'failed' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          We could not confirm whether this account already has a password, so this form is
          a best guess. Reload the page if it looks wrong.
        </Alert>
      )}

      {passwordPolicy && (
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2.5, color: 'text.secondary' }}>
          <Typography component="li" variant="caption">
            At least {passwordPolicy.minLength} characters
          </Typography>
          {passwordPolicy.requireUppercase && (
            <Typography component="li" variant="caption">
              One uppercase letter
            </Typography>
          )}
          {passwordPolicy.requireLowercase && (
            <Typography component="li" variant="caption">
              One lowercase letter
            </Typography>
          )}
          {passwordPolicy.requireNumbers && (
            <Typography component="li" variant="caption">
              One number
            </Typography>
          )}
          {passwordPolicy.requireSpecialChars && (
            <Typography component="li" variant="caption">
              One special character
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {needsCurrentPassword && (
        <PasswordInput
          variant="outlined"
          fullWidth
          margin="normal"
          label="Current password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          visibilityLabelNoun="current password"
          showPassword={showCurrent}
          onToggleShowPassword={() => setShowCurrent((v) => !v)}
        />
      )}

      <PasswordInput
        variant="outlined"
        fullWidth
        margin="normal"
        label={needsCurrentPassword ? 'New password' : 'Password'}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        autoComplete="new-password"
        visibilityLabelNoun="new password"
        showPassword={showNew}
        onToggleShowPassword={() => setShowNew((v) => !v)}
      />

      <PasswordInput
        variant="outlined"
        fullWidth
        margin="normal"
        label="Confirm password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        autoComplete="new-password"
        visibilityLabelNoun="password confirmation"
        showPassword={showConfirm}
        onToggleShowPassword={() => setShowConfirm((v) => !v)}
      />

      {beforeSubmit}

      <Box sx={{ mt: 2 }}>
        <StyledButton type="submit" variant="contained" disabled={busy}>
          {busy ? <CircularProgress size={20} color="inherit" /> : needsCurrentPassword ? 'Update password' : 'Set password'}
        </StyledButton>
      </Box>
    </Box>
  )
}

export default AccountPasswordPanel
